import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_pluginclient_7b5d99b3 from 'nuxt_plugin_pluginclient_7b5d99b3' // Source: ./plugin.client.js (mode: 'client')
import nuxt_plugin_pluginserver_3dd1e98a from 'nuxt_plugin_pluginserver_3dd1e98a' // Source: ./plugin.server.js (mode: 'server')
import nuxt_plugin_pluginstore_1bc3d5a2 from 'nuxt_plugin_pluginstore_1bc3d5a2' // Source: ./plugin.store.js (mode: 'all')
import nuxt_plugin_cookieuniversalnuxt_7687cc6a from 'nuxt_plugin_cookieuniversalnuxt_7687cc6a' // Source: ./cookie-universal-nuxt.js (mode: 'all')
import nuxt_plugin_sentryserver_a913e632 from 'nuxt_plugin_sentryserver_a913e632' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_45bc9b5f from 'nuxt_plugin_sentryclient_45bc9b5f' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_i18n_3c335d4e from 'nuxt_plugin_i18n_3c335d4e' // Source: ../common/nuxt/plugins/i18n (mode: 'all')
import nuxt_plugin_analytics_3e523656 from 'nuxt_plugin_analytics_3e523656' // Source: ../common/nuxt/plugins/analytics (mode: 'all')
import nuxt_plugin_abortcontrollerpolyfill_1b28d060 from 'nuxt_plugin_abortcontrollerpolyfill_1b28d060' // Source: ../common/nuxt/plugins/abortcontroller-polyfill (mode: 'all')
import nuxt_plugin_uniqueid_f9b63132 from 'nuxt_plugin_uniqueid_f9b63132' // Source: ../common/nuxt/plugins/unique-id (mode: 'all')
import nuxt_plugin_safehtml_15b1f758 from 'nuxt_plugin_safehtml_15b1f758' // Source: ../common/vue/directives/safe-html (mode: 'all')
import nuxt_plugin_accessiblemenu_33839682 from 'nuxt_plugin_accessiblemenu_33839682' // Source: ../common/vue/directives/accessible-menu (mode: 'all')
import nuxt_plugin_accessiblefeed_3389f604 from 'nuxt_plugin_accessiblefeed_3389f604' // Source: ../common/vue/directives/accessible-feed (mode: 'all')
import nuxt_plugin_clientnuxt_680bf6f3 from 'nuxt_plugin_clientnuxt_680bf6f3' // Source: ../cookie-manager/client-nuxt (mode: 'all')
import nuxt_plugin_hiringeasteregg_ae5e5522 from 'nuxt_plugin_hiringeasteregg_ae5e5522' // Source: ../common/nuxt/plugins/hiring-easteregg (mode: 'client')
import nuxt_plugin_lazyload_bc776cec from 'nuxt_plugin_lazyload_bc776cec' // Source: ../common/nuxt/plugins/lazyload (mode: 'client')
import nuxt_plugin_extension_7eb8b70f from 'nuxt_plugin_extension_7eb8b70f' // Source: ../common/nuxt/plugins/extension (mode: 'client')
import nuxt_plugin_vuetouchevents_04448bc4 from 'nuxt_plugin_vuetouchevents_04448bc4' // Source: ../search/images/src/plugins/vue-touch-events (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"http-equiv":"X-UA-Compatible","content":"IE=edge"},{"name":"theme-color","content":"#2e747a"},{"name":"referrer","content":"origin-when-cross-origin"},{"name":"keywords","content":"ecosia, green, search, engine"},{"property":"og:url","name":"og:url","content":"https:\u002F\u002Fwww.ecosia.org\u002F"},{"property":"og:image","name":"og:image","content":"https:\u002F\u002Fcdn-static.ecosia.org\u002Fog\u002Ffacebook.jpg"}],"link":[{"rel":"dns-prefetch","href":"https:\u002F\u002Fs3.amazonaws.com"},{"rel":"dns-prefetch","href":"https:\u002F\u002Fcdn-static.ecosia.org"},{"rel":"search","type":"application\u002Fopensearchdescription+xml","title":"Ecosia","href":"https:\u002F\u002Fcdn-static.ecosia.org\u002Fopensearch.xml"},{"rel":"shortcut icon","href":"https:\u002F\u002Fcdn-static.ecosia.org\u002Fassets\u002Fimages\u002Fico\u002Ffavicon.ico","type":"image\u002Fx-icon","sizes":"16x16 24x24 32x32 64x64"},{"rel":"apple-touch-icon","href":"https:\u002F\u002Fcdn-static.ecosia.org\u002Fassets\u002Fimages\u002Fpng\u002Fapple-touch-icon.png"},{"rel":"manifest","href":"https:\u002F\u002Fcdn-static.ecosia.org\u002Fmanifest.json"},{"rel":"preconnect","href":"https:\u002F\u002Fwww.bing.com"},{"rel":"preconnect","href":"https:\u002F\u002Ftse1.mm.bing.net"},{"rel":"preconnect","href":"https:\u002F\u002Ftse2.mm.bing.net"},{"rel":"preconnect","href":"https:\u002F\u002Ftse3.mm.bing.net"},{"rel":"preconnect","href":"https:\u002F\u002Ftse4.mm.bing.net"}],"titleTemplate":(chunk) => (chunk ? `${chunk} - Images` : 'Images'),"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.client && typeof nuxt_plugin_pluginclient_7b5d99b3 === 'function') {
    await nuxt_plugin_pluginclient_7b5d99b3(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_pluginserver_3dd1e98a === 'function') {
    await nuxt_plugin_pluginserver_3dd1e98a(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginstore_1bc3d5a2 === 'function') {
    await nuxt_plugin_pluginstore_1bc3d5a2(app.context, inject)
  }

  if (typeof nuxt_plugin_cookieuniversalnuxt_7687cc6a === 'function') {
    await nuxt_plugin_cookieuniversalnuxt_7687cc6a(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_sentryserver_a913e632 === 'function') {
    await nuxt_plugin_sentryserver_a913e632(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_45bc9b5f === 'function') {
    await nuxt_plugin_sentryclient_45bc9b5f(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_3c335d4e === 'function') {
    await nuxt_plugin_i18n_3c335d4e(app.context, inject)
  }

  if (typeof nuxt_plugin_analytics_3e523656 === 'function') {
    await nuxt_plugin_analytics_3e523656(app.context, inject)
  }

  if (typeof nuxt_plugin_abortcontrollerpolyfill_1b28d060 === 'function') {
    await nuxt_plugin_abortcontrollerpolyfill_1b28d060(app.context, inject)
  }

  if (typeof nuxt_plugin_uniqueid_f9b63132 === 'function') {
    await nuxt_plugin_uniqueid_f9b63132(app.context, inject)
  }

  if (typeof nuxt_plugin_safehtml_15b1f758 === 'function') {
    await nuxt_plugin_safehtml_15b1f758(app.context, inject)
  }

  if (typeof nuxt_plugin_accessiblemenu_33839682 === 'function') {
    await nuxt_plugin_accessiblemenu_33839682(app.context, inject)
  }

  if (typeof nuxt_plugin_accessiblefeed_3389f604 === 'function') {
    await nuxt_plugin_accessiblefeed_3389f604(app.context, inject)
  }

  if (typeof nuxt_plugin_clientnuxt_680bf6f3 === 'function') {
    await nuxt_plugin_clientnuxt_680bf6f3(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_hiringeasteregg_ae5e5522 === 'function') {
    await nuxt_plugin_hiringeasteregg_ae5e5522(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_lazyload_bc776cec === 'function') {
    await nuxt_plugin_lazyload_bc776cec(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_extension_7eb8b70f === 'function') {
    await nuxt_plugin_extension_7eb8b70f(app.context, inject)
  }

  if (typeof nuxt_plugin_vuetouchevents_04448bc4 === 'function') {
    await nuxt_plugin_vuetouchevents_04448bc4(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    router.push(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
